"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { AppleLogo } from "@/components/auth/SocialSignInButtons"
import { buttonVariants } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { trackFunnelEvent } from "@/lib/analytics"
import { APP_STORE_URL } from "@/lib/appStore"
import { BILLING_MODE } from "@/lib/billingMode"
import { cn } from "@/lib/utils"

export type AppStorePlacement = "hero" | "pricing" | "final_cta"

interface AppStoreLinkProps {
  placement: AppStorePlacement
  className?: string
}

/**
 * "Get it for iPhone" — a secondary outline link to the App Store listing. Rendered ONLY in app_store billing
 * mode (the Apple-billed site) AND only while `VITE_APP_STORE_URL` is set (lib/appStore.ts): before Apple approves
 * the app there is no live listing to point to, so the button must not exist. The click is a funnel event
 * (`app_store_clicked`), which also reaches Meta as the custom AppStoreClick when Marketing is consented.
 */
export function AppStoreLink({ placement, className }: AppStoreLinkProps) {
  const { t } = useI18n()
  if (BILLING_MODE !== "app_store" || !APP_STORE_URL) return null
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackFunnelEvent("app_store_clicked", { placement })}
      className={cn(buttonVariants({ variant: "outline", size: "lg" }), "gap-2 rounded-bb-full", className)}
    >
      <AppleLogo />
      {t("landingPage.appStoreCta")}
    </a>
  )
}
